import { useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useGeolocation } from '@/lib/contexts/device-permissions-context';
import { backendToFrontendFieldType } from '@/lib/utils/procedure-field-types';
import { buildAnswersFromFormData } from '@/lib/hooks/use-create-procedure-report';

/**
 * Hook partagé pour la gestion du formulaire de rapport de procédure.
 * Utilisé par ProcedureReportCreatePage et ProcedureReportEditPage.
 *
 * @param {object} options
 * @param {string} options.folderId - ID du dossier
 * @param {string} options.interventionId - ID de l'intervention/rapport
 * @param {object} options.configuration - Configuration du template (sections/fields)
 * @param {object} [options.initialFormData] - Données initiales du formulaire (flat: { fieldId: value })
 * @param {object} [options.initialPhotos] - Photos initiales { fieldId: [{ uniq_id, previewUrl }] }
 */
export function useProcedureReportForm({
    folderId,
    interventionId,
    configuration,
    initialFormData = {},
    initialPhotos = {},
}) {
    const { t } = useTranslation();
    const { position } = useGeolocation();

    // État du formulaire (toujours flat: { fieldId: value })
    const [formData, setFormData] = useState(initialFormData);

    // Photos uploadées par champ { [fieldId]: [{ uniq_id, previewUrl }] }
    const [uploadedPhotos, setUploadedPhotos] = useState(initialPhotos);

    // Capture photo
    const [showPhotoCapture, setShowPhotoCapture] = useState(false);
    const [captureFieldId, setCaptureFieldId] = useState(null);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(null);

    // Sections mémorisées
    const sections = useMemo(() => configuration?.sections || [], [configuration]);

    // Nombre total de photos
    const totalPhotoCount = useMemo(
        () => Object.values(uploadedPhotos).reduce((sum, photos) => sum + photos.length, 0),
        [uploadedPhotos],
    );

    // ============ CHAMPS ============
    const handleFieldChange = useCallback((fieldId, value) => {
        setFormData((prev) => ({ ...prev, [fieldId]: value }));
    }, []);

    // ============ PHOTOS ============
    const handlePhotoCapture = useCallback((fieldId) => {
        setCaptureFieldId(fieldId);
        setShowPhotoCapture(true);
    }, []);

    const handlePhotoCaptured = useCallback(
        async ({ mediaType = 'photo', base64, blob, fileName, dataUrl, mimeType, durationMs }) => {
            console.log('[useProcedureReportForm] handlePhotoCaptured', {
                mediaType,
                base64,
                blob,
                fileName,
                dataUrl,
                mimeType,
                durationMs,
            });
            if (!folderId || !interventionId || !captureFieldId) {
                toast.error(t('procedures:report.missingData'));
                return;
            }

            setShowPhotoCapture(false);
            setIsUploadingPhoto(captureFieldId);

            try {
                const isVideo = mediaType === 'video';
                const payload = {
                    file_name: fileName,
                    latitude: position?.latitude,
                    longitude: position?.longitude,
                    field_id: Number(captureFieldId),
                    ...(isVideo ? { video: blob, mime_type: mimeType, duration_ms: durationMs } : { base64 }),
                };
                console.log('[useProcedureReportForm] uploadInterventionPicture payload', payload);
                const response = await horodatyApi.uploadInterventionPicture(folderId, interventionId, payload);

                const uploadedDoc = response.data?.data;

                setUploadedPhotos((prev) => ({
                    ...prev,
                    [captureFieldId]: [
                        ...(prev[captureFieldId] || []),
                        {
                            uniq_id: uploadedDoc?.uniq_id,
                            previewUrl: dataUrl,
                            mediaType,
                        },
                    ],
                }));

                toast.success(isVideo ? t('procedures:report.videoTimestamped') : t('procedures:report.photoTimestamped'));
            } catch (err) {
                console.error('Erreur upload média:', err);
                toast.error(t('procedures:report.mediaUploadFailed'));
            } finally {
                setIsUploadingPhoto(null);
                setCaptureFieldId(null);
            }
        },
        [folderId, interventionId, captureFieldId, position, t],
    );

    const handleRemovePhoto = useCallback((fieldId, photoIndex) => {
        setUploadedPhotos((prev) => ({
            ...prev,
            [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== photoIndex),
        }));
    }, []);

    const closePhotoCapture = useCallback(() => {
        setShowPhotoCapture(false);
        setCaptureFieldId(null);
    }, []);

    // ============ ANSWERS ============
    /** Construit l'objet answers avec les photos incluses */
    const buildAnswersWithPhotos = useCallback(() => {
        if (!configuration) return null;

        const answers = buildAnswersFromFormData(configuration, formData);

        answers.sections.forEach((section) => {
            section.fields.forEach((field) => {
                const frontendType = backendToFrontendFieldType(field.type);
                if (frontendType === 'photo' || frontendType === 'video') {
                    const photos = uploadedPhotos[field.id] || [];
                    field.value = photos.map((p) => p.uniq_id);
                }
            });
        });

        return answers;
    }, [configuration, formData, uploadedPhotos]);

    // ============ VALIDATION ============
    /** Valide les champs photo/vidéo requis */
    const validatePhotoFields = useCallback(() => {
        const missingFields = [];

        sections.forEach((section) => {
            (section.fields || []).forEach((field) => {
                const frontendType = backendToFrontendFieldType(field.type);
                const isMedia = frontendType === 'photo' || frontendType === 'video';
                if (isMedia && field.required) {
                    const items = uploadedPhotos[field.id] || [];
                    const minItems =
                        (frontendType === 'video' ? field.minVideos : field.minPhotos) || 1;
                    if (items.length < minItems) {
                        missingFields.push(field);
                    }
                }
            });
        });

        return { isValid: missingFields.length === 0, missingFields };
    }, [sections, uploadedPhotos]);

    /** Vérifie si une section donnée est valide */
    const isSectionValid = useCallback(
        (section) => {
            return (section.fields || []).every((field) => {
                if (!field.required) return true;
                const frontendType = backendToFrontendFieldType(field.type);
                if (frontendType === 'photo' || frontendType === 'video') {
                    const minItems =
                        (frontendType === 'video' ? field.minVideos : field.minPhotos) || 1;
                    const items = uploadedPhotos[field.id] || [];
                    return items.length >= minItems;
                }
                const value = formData[field.id];
                if (Array.isArray(value)) return value.length > 0;
                return value && value.toString().trim() !== '';
            });
        },
        [formData, uploadedPhotos],
    );

    return {
        // State
        formData,
        setFormData,
        uploadedPhotos,
        setUploadedPhotos,
        sections,
        totalPhotoCount,

        // Field handlers
        handleFieldChange,

        // Photo handlers
        showPhotoCapture,
        captureFieldId,
        isUploadingPhoto,
        handlePhotoCapture,
        handlePhotoCaptured,
        handleRemovePhoto,
        closePhotoCapture,

        // Answers & validation
        buildAnswersWithPhotos,
        validatePhotoFields,
        isSectionValid,
    };
}
